import { api } from '@/api';
import type {
  Exception,
  I18nInfo as GeneratedI18nInfo,
  Ptw100CsoPortalBadGatewayException,
  Ptw100CsoPortalServiceUnavailableException,
} from '@/api/generator';
import { catchAllErrorHandler, stopErrorPropagation } from '@/errorHandling';
import { AnyApiError, ApiError } from '@/errorHandling/typeGuards';
import { I18nInfo, I18nKey } from '@/utils/typings';
import { QaEquipmentAddFilterObject } from '@/views/qaEquipment/types';
import { RegisterableQaEquipment } from '@/views/qaEquipment/utils';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useNotificationStore } from '@/stores/notificationStore';
import { notificationForError } from '@/components/notifications/utils';
import { type NotificationEntry } from '@/components/notifications/types';
import { useToken } from '@/utils/tokens/tokens';

type DetailedError = {
  message: I18nInfo | string;
  response?: CsoPortalException;
  remedyI18n?: I18nInfo;
  /** Untranslated upstream CSO Portal message, used as the notification body when the backend supplied no remedy key. */
  detail?: string;
};

/**
 * The two CSO Portal proxy exceptions declared on `QaEquipmentCloudApi`'s register/deregister
 * operations. Both carry `remedy: CsoPortalErrorDto`, holding the discriminating i18n key and
 * the resolved remedy translation key.
 */
type CsoPortalException =
  | Ptw100CsoPortalBadGatewayException
  | Ptw100CsoPortalServiceUnavailableException;

/**
 * Narrows the generated `key: string` to the `I18nKey` i18next expects. Keeps the
 * key/parameters pair instead of translating eagerly, so callers translating via `$t()` still
 * render correctly after a locale switch.
 */
function toFrontendI18n(i18n?: GeneratedI18nInfo): I18nInfo | undefined {
  return i18n?.key ? { key: i18n.key as I18nKey, parameters: i18n.parameters as never } : undefined;
}

/** Single source for both the runtime check and the narrowed type below. */
const CSO_PORTAL_ERROR_NAMES = [
  ApiError.csoPortalBadGateway,
  ApiError.csoPortalServiceUnavailable,
] as const;

type CsoPortalError = Extract<AnyApiError, { name: (typeof CSO_PORTAL_ERROR_NAMES)[number] }>;

function isCsoPortalError(error: AnyApiError): error is CsoPortalError {
  return CSO_PORTAL_ERROR_NAMES.some((name) => name === error.name);
}

/**
 * `remedy.i18n` identifies the upstream CSO Portal failure (NotFound, WrongDpin, ...) and
 * `remedy.remedy` is its resolved advice key; `exception.i18n` is the generic outer key, used
 * only when there is nothing more specific; `detail` is the untranslated upstream message from
 * `innerException`.
 */
function readCsoPortalFailure(error: CsoPortalError) {
  const exception = error.error?.exception;
  const remedy = exception?.remedy?.remedy;

  return {
    exception,
    i18n: toFrontendI18n(exception?.remedy?.i18n ?? exception?.i18n),
    remedyI18n: remedy ? { key: remedy as I18nKey } : undefined,
    detail: exception?.innerException?.message ?? undefined,
  };
}

export const useQaEquipmentStore = defineStore('qaEquipment', () => {
  const addConfigurationFilterObject = ref<QaEquipmentAddFilterObject>({});

  const deviceForRegistration = ref<
    (RegisterableQaEquipment & { dpin?: string | null }) | undefined
  >();
  const registrationSuccess = ref(() => {});
  const registrationFailure = ref(() => {});
  const deRegistrationSuccess = ref(() => {});
  const deRegistrationFailure = ref(() => {});
  const registrationPending = ref(false);
  const deRegistrationPending = ref(false);
  const csoPortalFullErrorResponse = ref<Exception>();
  const detailedError = ref<DetailedError>();
  const csoPortalErrorMessage = computed(() => {
    if (csoPortalFullErrorResponse.value) {
      return `${JSON.stringify(csoPortalFullErrorResponse.value, null, 2)}`;
    }
    return '';
  });

  // =============================== Actions ===================================

  function setDeviceForRegistration(newValue?: RegisterableQaEquipment) {
    deviceForRegistration.value = newValue;
  }
  function setFilterObject(newValue: QaEquipmentAddFilterObject) {
    addConfigurationFilterObject.value = newValue;
  }
  function setCsoPortalFullErrorResponse(newValue?: Exception) {
    csoPortalFullErrorResponse.value = newValue;
  }

  // ====================== Registration & De-Registration =====================

  function handleRegistrationError(error: AnyApiError): AnyApiError {
    if (isCsoPortalError(error)) {
      const { exception, i18n, remedyI18n, detail } = readCsoPortalFailure(error);
      csoPortalFullErrorResponse.value = exception?.innerException;

      if (i18n) {
        detailedError.value = { message: i18n, response: exception, remedyI18n, detail };
      } else if (exception?.message) {
        detailedError.value = { message: exception.message, response: exception };
      } else {
        detailedError.value = { message: 'Unknown error', response: exception };
      }
    }

    return error;
  }

  const errorNotification = ref<NotificationEntry>();
  async function catchAllRegistrationErrors(error: AnyApiError) {
    await catchAllErrorHandler(error);
    errorNotification.value = useNotificationStore().getMessageEntry(notificationForError(error));
    // do not overwrite the detailed error if it is the same as the one from the error notification
    if (
      errorNotification.value?.message &&
      errorNotification.value.message === detailedError.value?.message
    ) {
      detailedError.value = {
        message: errorNotification.value.message,
      };
    }
    return error;
  }

  async function register(registerEntity: RegisterableQaEquipment) {
    if (errorNotification.value?.id) {
      useNotificationStore().removeMessage(errorNotification.value?.id);
      errorNotification.value = undefined;
    }
    detailedError.value = undefined;
    csoPortalFullErrorResponse.value = undefined;
    if (registerEntity.id) {
      const token = useToken(registerEntity);

      token.on.inUse(() => {
        detailedError.value = {
          message: { key: 'EntityIsInEditingState' },
          response: undefined,
        };
      });

      await token.request();

      registrationPending.value = true;
      const { success } = await api.qaEquipmentCloudApi.registerQaEquipment(
        {
          id: registerEntity.id,
        },
        [handleRegistrationError, catchAllRegistrationErrors, stopErrorPropagation],
      );

      if (success) {
        registrationSuccess.value?.();
      } else {
        registrationFailure.value?.();
      }
      registrationPending.value = false;
      await token.revoke();
    }
  }
  function handleDeregistrationError(error: AnyApiError): AnyApiError {
    const defaultMsg = { key: 'DeRegistrationFailed' } as const;

    if (isCsoPortalError(error)) {
      const { exception, i18n, remedyI18n, detail } = readCsoPortalFailure(error);

      detailedError.value = {
        message: i18n ?? exception?.message ?? defaultMsg,
        response: exception,
        remedyI18n,
        detail,
      };
    } else {
      detailedError.value = { message: defaultMsg };
    }

    return error;
  }
  async function deregister(deRegisterEntity: RegisterableQaEquipment) {
    if (deRegisterEntity.id) {
      const token = useToken(deRegisterEntity);

      token.on.inUse(() => {
        detailedError.value = {
          message: {
            key: 'EntityIsInEditingState',
          },
          response: undefined,
        };
      });

      await token.request();

      if (token.isValid.value) {
        deRegistrationPending.value = true;

        const { success } = await api.qaEquipmentCloudApi.deRegisterQaEquipment(
          {
            id: deRegisterEntity.id,
          },
          [handleDeregistrationError, stopErrorPropagation],
        );

        if (success) {
          deRegistrationSuccess.value?.();
        } else {
          deRegistrationFailure.value?.();
        }

        deRegistrationPending.value = false;

        await token.revoke();
      }
    }
  }

  // =============================== Error display =============================
  function resetErrorMessage() {
    detailedError.value = undefined;
    csoPortalFullErrorResponse.value = undefined;
  }

  return {
    addConfigurationFilterObject,
    deviceForRegistration,
    register,
    deregister,
    registrationPending,
    deRegistrationPending,
    detailedError,
    csoPortalErrorMessage,
    resetErrorMessage,
    registrationSuccess,
    registrationFailure,
    deRegistrationSuccess,
    deRegistrationFailure,
    csoPortalFullErrorResponse,
    setDeviceForRegistration,
    setFilterObject,
    setCsoPortalFullErrorResponse,
    handleRegistrationError,
    handleDeregistrationError,
  };
});
